import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import type { AppointmentPaymentDetails } from '../services/api';
import { getApiErrorMessage } from '../types/api-errors';

export interface TransactionFilters {
    status?: string;
    startDate?: string;
    endDate?: string;
}

export const usePayments = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getTransactions = useCallback(async (page = 1, pageSize = 20, filters?: TransactionFilters) => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getOwnerTransactions(page, pageSize, filters);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load transactions.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getCommissions = useCallback(async (page = 1, pageSize = 20) => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getOwnerCommissions(page, pageSize);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load commissions.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getPayouts = useCallback(async (page = 1, pageSize = 20) => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getOwnerPayouts(page, pageSize);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load payouts.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePayoutIban = useCallback(async (iban: string) => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.updatePayoutIban(iban.trim());
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to update IBAN.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getAppointmentPaymentDetails = useCallback(async (appointmentId: number): Promise<AppointmentPaymentDetails | null> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getAppointmentPaymentDetails(appointmentId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load payment details.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, getTransactions, getCommissions, getPayouts, updatePayoutIban, getAppointmentPaymentDetails };
};
